import React, { useEffect, useState, useMemo } from 'react';
import { ref, onValue, update, push, uploadBytes, getDownloadURL } from 'firebase/database';
import { ref as storageRef, uploadBytes as storageUploadBytes, getDownloadURL as storageGetDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import imageCompression from 'browser-image-compression';
import { 
  FiX, 
  FiDollarSign, 
  FiCalendar, 
  FiUser, 
  FiPackage, 
  FiDownload,
  FiFilter,
  FiSearch,
  FiAlertCircle,
  FiCheckCircle,
  FiMapPin,
  FiPhone,
  FiMail,
  FiEdit3,
  FiUpload,
  FiImage
} from 'react-icons/fi';
import { CSVLink } from 'react-csv';

const OrderCancelReport = () => {
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  // Refund form state
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundFormData, setRefundFormData] = useState({
    refundMethod: '',
    utrNumber: '',
    proofImage: null,
    proofImageUrl: '',
    refundAmount: 0
  });
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') return address;
    
    const { street, landmark, city, state, postalCode } = address;
    const parts = [street, landmark, city, state, postalCode].filter(Boolean);
    return parts.join(', ') || 'N/A';
  };

  // Image compression function to 30-50KB
  const compressImage = async (imageFile) => {
    try {
      setCompressing(true);
      const options = {
        maxSizeMB: 0.05, // 50KB max
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.7,
      };

      const compressedFile = await imageCompression(imageFile, options);
      
      console.log('Original size:', (imageFile.size / 1024).toFixed(2), 'KB');
      console.log('Compressed size:', (compressedFile.size / 1024).toFixed(2), 'KB');

      // If still larger than 50KB, compress more aggressively
      if (compressedFile.size > 50 * 1024) {
        const moreCompressed = await imageCompression(compressedFile, {
          maxSizeMB: 0.03, // 30KB
          maxWidthOrHeight: 600,
          useWebWorker: true,
          initialQuality: 0.5,
        });
        setCompressing(false);
        return moreCompressed;
      }

      setCompressing(false);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      setCompressing(false);
      alert('Failed to compress image');
      return null;
    }
  };

  // Handle image selection and compression
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB original)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size should be less than 10MB');
      return;
    }

    // Compress the image
    const compressedFile = await compressImage(file);
    if (!compressedFile) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(compressedFile);

    setRefundFormData(prev => ({
      ...prev,
      proofImage: compressedFile
    }));
  };

  useEffect(() => {
    const ordersRef = ref(db, 'HTAMS/orders/');
    
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const cancelled = Object.entries(data)
          .map(([id, order]) => ({
            id,
            ...order,
            cancelledDate: order.cancelledAt || order.cancelledDate,
          }))
          .filter(order => order.status?.toLowerCase() === 'cancelled')
          .sort((a, b) => {
            const dateA = new Date(a.cancelledAt || a.cancelledDate || 0);
            const dateB = new Date(b.cancelledAt || b.cancelledDate || 0);
            return dateB - dateA;
          });
        
        setCancelledOrders(cancelled);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const analytics = useMemo(() => {
    const totalCancelled = cancelledOrders.length;
    const totalRefunded = cancelledOrders.filter(o => o.refundStatus === 'refunded').length;
    const pendingRefund = cancelledOrders.filter(o => o.refundStatus === 'pending' || !o.refundStatus).length;
    
    const totalAmount = cancelledOrders.reduce((sum, order) => {
      return sum + (order.totalAmount || order.amount || 0);
    }, 0);
    
    const refundedAmount = cancelledOrders
      .filter(o => o.refundStatus === 'refunded')
      .reduce((sum, order) => sum + (order.totalAmount || order.amount || 0), 0);
    
    const pendingAmount = cancelledOrders
      .filter(o => o.refundStatus === 'pending' || !o.refundStatus)
      .reduce((sum, order) => sum + (order.totalAmount || order.amount || 0), 0);

    return {
      totalCancelled,
      totalRefunded,
      pendingRefund,
      totalAmount,
      refundedAmount,
      pendingAmount,
    };
  }, [cancelledOrders]);

  const filteredOrders = useMemo(() => {
    return cancelledOrders.filter(order => {
      const matchesRefundStatus = 
        filterStatus === 'all' ? true :
        filterStatus === 'refunded' ? order.refundStatus === 'refunded' :
        filterStatus === 'pending' ? (order.refundStatus === 'pending' || !order.refundStatus) :
        true;

      const matchesSearch = searchQuery
        ? order.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.customerPhone?.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      const matchesDateRange = (() => {
        if (!dateRange.start || !dateRange.end) return true;
        const orderDate = new Date(order.cancelledAt || order.cancelledDate);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        return orderDate >= startDate && orderDate <= endDate;
      })();

      return matchesRefundStatus && matchesSearch && matchesDateRange;
    });
  }, [cancelledOrders, filterStatus, searchQuery, dateRange]);

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ordersPerPage);

  const handleOpenRefundForm = (order) => {
    setSelectedOrder(order);
    setShowRefundForm(true);
    setRefundFormData({
      refundMethod: '',
      utrNumber: '',
      proofImage: null,
      proofImageUrl: '',
      refundAmount: order.totalAmount || order.amount || 0
    });
    setImagePreview('');
  };

  // Handle refund form submission with image upload
  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    
    if (!refundFormData.refundMethod) {
      alert('Please select a refund method');
      return;
    }

    // Validate based on method
    if (refundFormData.refundMethod === 'upi' && !refundFormData.utrNumber.trim()) {
      alert('Please enter UTR number for UPI refund');
      return;
    }

    if (refundFormData.refundMethod === 'bank_transfer' && !refundFormData.proofImage) {
      alert('Please upload bank transfer proof image');
      return;
    }

    setUploading(true);

    try {
      let proofImageUrl = '';

      // Upload image to Firebase Storage if bank transfer
      if (refundFormData.refundMethod === 'bank_transfer' && refundFormData.proofImage) {
        const imageRef = storageRef(storage, `refund-proofs/${selectedOrder.id}_${Date.now()}.jpg`);
        await storageUploadBytes(imageRef, refundFormData.proofImage);
        proofImageUrl = await storageGetDownloadURL(imageRef);
        console.log('Image uploaded:', proofImageUrl);
      }

      const updates = {
        refundStatus: 'refunded',
        refundProcessedAt: new Date().toISOString(),
        refundProcessedBy: 'admin',
        refundMethod: refundFormData.refundMethod,
        refundAmount: refundFormData.refundAmount,
      };

      // Add method-specific fields
      if (refundFormData.refundMethod === 'upi') {
        updates.utrNumber = refundFormData.utrNumber;
      } else if (refundFormData.refundMethod === 'bank_transfer') {
        updates.proofImageUrl = proofImageUrl;
      }

      await update(ref(db, `HTAMS/orders/${selectedOrder.id}`), updates);

      // Log refund action
      const refundLogRef = push(ref(db, 'HTAMS/cancellationReports'));
      await update(refundLogRef, {
        orderId: selectedOrder.id,
        action: 'refunded',
        timestamp: new Date().toISOString(),
        amount: refundFormData.refundAmount,
        refundMethod: refundFormData.refundMethod,
        utrNumber: refundFormData.utrNumber || null,
        proofImageUrl: proofImageUrl || null,
        processedBy: 'admin',
      });

      setSelectedOrder(null);
      setShowRefundForm(false);
      setUploading(false);
      alert('Refund processed successfully!');
    } catch (error) {
      console.error('Error processing refund:', error);
      setUploading(false);
      alert('Failed to process refund: ' + error.message);
    }
  };

  const handleRejectRefund = async () => {
    const reason = window.prompt('Please enter reason for rejection:');
    if (!reason) return;

    try {
      const updates = {
        refundStatus: 'rejected',
        refundProcessedAt: new Date().toISOString(),
        refundProcessedBy: 'admin',
        rejectionReason: reason,
      };

      await update(ref(db, `HTAMS/orders/${selectedOrder.id}`), updates);

      const refundLogRef = push(ref(db, 'HTAMS/cancellationReports'));
      await update(refundLogRef, {
        orderId: selectedOrder.id,
        action: 'rejected',
        timestamp: new Date().toISOString(),
        amount: selectedOrder.totalAmount || selectedOrder.amount,
        reason: reason,
        processedBy: 'admin',
      });

      setSelectedOrder(null);
      setShowRefundForm(false);
      alert('Refund rejected');
    } catch (error) {
      console.error('Error rejecting refund:', error);
      alert('Failed to reject refund');
    }
  };

  const csvData = filteredOrders.map((order, index) => ({
    'Sr No': index + 1,
    'Order ID': order.id,
    'Customer Name': order.customerName,
    'Phone': order.customerPhone || 'N/A',
    'Product': order.items?.map(i => i.productName).join(', ') || order.productName,
    'Amount': order.totalAmount || order.amount,
    'Cancelled Date': order.cancelledAt ? new Date(order.cancelledAt).toLocaleDateString() : 'N/A',
    'Refund Status': order.refundStatus || 'Pending',
    'Refund Method': order.refundMethod || 'N/A',
    'UTR Number': order.utrNumber || 'N/A',
    'Cancellation Reason': order.cancellationReason || 'Not specified',
  }));

  if (loading) {
    return (
      <div className="cancel-report-container">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading cancellation reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cancel-report-container">
      {/* Header - Same as before */}
      <div className="report-header">
        <div className="header-left">
          <h1 className="report-title">
            <FiX className="title-icon" />
            Order Cancellation Reports
          </h1>
          <p className="report-subtitle">Track cancelled orders & refunds</p>
        </div>
        <CSVLink
          data={csvData}
          filename={`cancellation-report-${new Date().toISOString().split('T')[0]}.csv`}
          className="export-btn"
        >
          <FiDownload /> Export
        </CSVLink>
      </div>

      {/* Statistics - Same as before */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-content">
            <div className="stat-header">
              {/* <FiX className="stat-icon" /> */}
              <span className="stat-label">Total Cancelled</span>
            </div>
            <div className="stat-number">{analytics.totalCancelled}</div>
            <div className="stat-amount">₹{analytics.totalAmount.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card refunded">
          <div className="stat-content">
            <div className="stat-header">
              <FiCheckCircle className="stat-icon" />
              <span className="stat-label">Refunded</span>
            </div>
            <div className="stat-number">{analytics.totalRefunded}</div>
            <div className="stat-amount">₹{analytics.refundedAmount.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="stat-content">
            <div className="stat-header">
              <FiAlertCircle className="stat-icon" />
              <span className="stat-label">Pending Refund</span>
            </div>
            <div className="stat-number">{analytics.pendingRefund}</div>
            <div className="stat-amount">₹{analytics.pendingAmount.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Filters - Same as before */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filterStatus === 'refunded' ? 'active' : ''}`}
              onClick={() => setFilterStatus('refunded')}
            >
              Refunded
            </button>
            <button
              className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              Pending
            </button>
          </div>

          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="date-wrapper">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="date-input"
            />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {/* Table - Same as before */}
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
              <th>Method</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order, index) => (
              <tr key={order.id}>
                <td>{startIndex + index + 1}</td>
                <td>
                  <div className="customer-info">
                    <div className="customer-name">{order.customerName}</div>
                    <div className="customer-phone">{order.customerPhone}</div>
                  </div>
                </td>
                <td className="product-cell">
                  {order.items && order.items.length > 0
                    ? `${order.items[0].productName}${order.items.length > 1 ? ` +${order.items.length - 1}` : ''}`
                    : order.productName || 'N/A'}
                </td>
                <td className="amount">₹{(order.totalAmount || order.amount || 0).toLocaleString()}</td>
                <td>
                  {order.cancelledAt 
                    ? new Date(order.cancelledAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit'
                      })
                    : 'N/A'}
                </td>
                <td>
                  <span className={`status-badge ${order.refundStatus || 'pending'}`}>
                    {order.refundStatus === 'refunded' ? 'Refunded' : 'Pending'}
                  </span>
                </td>
                <td className="method-cell">
                  {order.refundMethod ? (order.refundMethod === 'upi' ? 'UPI' : 'Bank Transfer') : 'N/A'}
                </td>
                <td>
                  <button
                    className="action-btn"
                    onClick={() => handleOpenRefundForm(order)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrders.length === 0 && (
        <div className="empty-state">
          <FiX className="empty-icon" />
          <h3>No cancelled orders found</h3>
        </div>
      )}

      {/* Pagination - Same as before */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal with Updated Refund Form */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => {
          setSelectedOrder(null);
          setShowRefundForm(false);
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details</h3>
              <button className="close-btn" onClick={() => {
                setSelectedOrder(null);
                setShowRefundForm(false);
              }}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="status-section">
                <span className={`large-badge ${selectedOrder.refundStatus || 'pending'}`}>
                  {selectedOrder.refundStatus === 'refunded' ? (
                    <><FiCheckCircle /> Refunded</>
                  ) : (
                    <><FiAlertCircle /> Pending Refund</>
                  )}
                </span>
              </div>

              <div className="details-grid">
                <div className="detail-column">
                  <div className="detail-group">
                    <h4><FiUser /> Customer Details</h4>
                    <div className="detail-row">
                      <span className="label">Name:</span>
                      <span className="value">{selectedOrder.customerName}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label"><FiPhone /> Phone:</span>
                      <span className="value">{selectedOrder.customerPhone || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label"><FiMail /> Email:</span>
                      <span className="value">{selectedOrder.customerEmail || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label"><FiMapPin /> Address:</span>
                      <span className="value">{formatAddress(selectedOrder.customerAddress)}</span>
                    </div>
                  </div>

                  <div className="detail-group">
                    <h4><FiCalendar /> Cancellation Info</h4>
                    <div className="detail-row">
                      <span className="label">Date:</span>
                      <span className="value">
                        {selectedOrder.cancelledAt 
                          ? new Date(selectedOrder.cancelledAt).toLocaleString('en-IN')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Reason:</span>
                      <span className="value">{selectedOrder.cancellationReason || 'Not specified'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-column">
                  <div className="detail-group">
                    <h4><FiPackage /> Order Details</h4>
                    <div className="detail-row">
                      <span className="label">Order ID:</span>
                      <span className="value">{selectedOrder.id}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Products:</span>
                      <div className="products-list">
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, i) => (
                            <div key={i} className="product-item">
                              {item.productName} × {item.quantity} - ₹{item.totalPrice?.toLocaleString() || 0}
                            </div>
                          ))
                        ) : (
                          <span>{selectedOrder.productName || 'N/A'}</span>
                        )}
                      </div>
                    </div>
                    <div className="detail-row">
                      <span className="label">Total Amount:</span>
                      <span className="value amount-lg">
                        ₹{(selectedOrder.totalAmount || selectedOrder.amount || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="detail-group">
                    <h4><FiDollarSign /> Refund Status</h4>
                    <div className="detail-row">
                      <span className="label">Status:</span>
                      <span className={`status-badge ${selectedOrder.refundStatus || 'pending'}`}>
                        {selectedOrder.refundStatus || 'Pending'}
                      </span>
                    </div>
                    {selectedOrder.refundMethod && (
                      <div className="detail-row">
                        <span className="label">Method:</span>
                        <span className="value">
                          {selectedOrder.refundMethod === 'upi' ? 'UPI' : 'Bank Transfer'}
                        </span>
                      </div>
                    )}
                    {selectedOrder.utrNumber && (
                      <div className="detail-row">
                        <span className="label">UTR Number:</span>
                        <span className="value">{selectedOrder.utrNumber}</span>
                      </div>
                    )}
                    {selectedOrder.proofImageUrl && (
                      <div className="detail-row">
                        <span className="label">Proof:</span>
                        <a href={selectedOrder.proofImageUrl} target="_blank" rel="noopener noreferrer" className="image-link">
                          <FiImage /> View Image
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Updated Refund Form */}
              {(!selectedOrder.refundStatus || selectedOrder.refundStatus === 'pending') && showRefundForm && (
                <form onSubmit={handleRefundSubmit} className="refund-form">
                  <h4 className="form-title">
                    <FiEdit3 /> Process Refund
                  </h4>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <FiDollarSign /> Refund Amount
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={refundFormData.refundAmount}
                        onChange={(e) => setRefundFormData(prev => ({
                          ...prev,
                          refundAmount: Number(e.target.value)
                        }))}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiDollarSign /> Refund Method *
                      </label>
                      <select
                        className="form-select"
                        value={refundFormData.refundMethod}
                        onChange={(e) => {
                          setRefundFormData(prev => ({
                            ...prev,
                            refundMethod: e.target.value,
                            utrNumber: '',
                            proofImage: null
                          }));
                          setImagePreview('');
                        }}
                        required
                      >
                        <option value="">Select method</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="upi">UPI</option>
                      </select>
                    </div>
                  </div>

                  {/* Conditional fields based on method */}
                  {refundFormData.refundMethod === 'upi' && (
                    <div className="form-group">
                      <label className="form-label">
                        <FiEdit3 /> UTR Number *
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter 12-digit UTR number"
                        value={refundFormData.utrNumber}
                        onChange={(e) => setRefundFormData(prev => ({
                          ...prev,
                          utrNumber: e.target.value
                        }))}
                        maxLength="12"
                        required
                      />
                      <small className="form-hint">
                        Example: 412345678901
                      </small>
                    </div>
                  )}

                  {refundFormData.refundMethod === 'bank_transfer' && (
                    <div className="form-group">
                      <label className="form-label">
                        <FiUpload /> Upload Transfer Proof *
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="file-input"
                        required
                        disabled={compressing || uploading}
                      />
                      <small className="form-hint">
                        {compressing ? '⏳ Compressing image to 30-50KB...' : 'Image will be auto-compressed to 30-50KB'}
                      </small>
                      
                      {imagePreview && (
                        <div className="image-preview">
                          <img src={imagePreview} alt="Proof" />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => {
                              setImagePreview('');
                              setRefundFormData(prev => ({ ...prev, proofImage: null }));
                            }}
                          >
                            <FiX /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-submit"
                      disabled={uploading || compressing}
                    >
                      {uploading ? (
                        <>⏳ Processing...</>
                      ) : (
                        <><FiCheckCircle /> Confirm Refund</>
                      )}
                    </button>
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => setShowRefundForm(false)}
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {(!selectedOrder.refundStatus || selectedOrder.refundStatus === 'pending') && !showRefundForm && (
                <div className="action-buttons">
                  <button
                    className="btn-refund"
                    onClick={() => setShowRefundForm(true)}
                  >
                    <FiCheckCircle /> Process Refund
                  </button>
                  <button
                    className="btn-reject"
                    onClick={handleRejectRefund}
                  >
                    <FiX /> Reject Refund
                  </button>
                </div>
              )}

              {selectedOrder.refundStatus === 'refunded' && (
                <div className="refund-success">
                  <FiCheckCircle /> Refund completed via {selectedOrder.refundMethod === 'upi' ? 'UPI' : 'Bank Transfer'}
                  {selectedOrder.utrNumber && ` (UTR: ${selectedOrder.utrNumber})`}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        .cancel-report-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #f5f5f5;
         
          min-height: 100vh;
        }

        .loading-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e0e0e0;
          border-top-color: #ef4444;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 12px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .report-header {
        background: var(--mobile-bg-primary);
          display: flex;
          justify-content: space-between;
          align-items: center;
         
          padding: 16px 20px;
          border-radius: 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
        }

        .header-left {
          flex: 1;
        }

        .report-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--mobile-text-primary);
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .title-icon {
          color: #ef4444;
          font-size: 1.4rem;
        }
          .report-table thead{
            background: var(--mobile-bg-primary);
          }

        .report-subtitle {
          color: var(--mobile-text-secondary);
          margin: 0;
          font-size: 0.875rem;
        }

        .export-btn {
          background: #6366f1;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          text-decoration: none;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .export-btn:hover {
          background: #4f46e5;
          transform: translateY(-1px);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
          min-height: 200px;
          max-height: 100px;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin:0;
        }

        .stat-icon {
          font-size: 1.2rem;
        }
        .stat-card.total .stat-icon { color: #ef4444; }
        .stat-card.refunded .stat-icon { color: #10b981; }
        .stat-card.pending .stat-icon { color: #f59e0b; }

        .stat-label {
          font-size: 0.75rem;
          color: black;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.5px;
          margin: 0;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .stat-amount {
          font-size: 1rem;
          font-weight: 600;
          color: #10b981;
        }

        .filters-section {
          background: white;
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
        }

        .filters-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .filter-buttons {
          display: flex;
          gap: 8px;
        }

        .filter-btn {
          background: #f5f5f5;
          color: #666;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .filter-btn:hover,
        .filter-btn.active {
          background: #6366f1;
          color: white;
        }

        .search-wrapper {
          flex: 1;
          min-width: 200px;
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .search-input {
          width: 100%;
          padding: 8px 12px 8px 36px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 0.875rem;
          outline: none;
        }

        .search-input:focus {
          border-color: #6366f1;
        }

        .date-wrapper {
          display: flex;
          gap: 8px;
        }

        .date-input {
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 0.875rem;
          outline: none;
        }

        .date-input:focus {
          border-color: #6366f1;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
        }

        .report-table {
          width: 100%;
          border-collapse: collapse;
        }

        .report-table th {
          background: var(--mobile-bg-primary);
          color: var(--mobile-text-primary);
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .report-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.875rem;
        }

        .report-table tr:hover {
          background: #fafafa;
        }

        .customer-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .customer-name {
          font-weight: 600;
          color: #1a1a1a;
        }

        .customer-phone {
          font-size: 0.75rem;
          color: #999;
        }

        .product-cell {
          max-width: 200px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .method-cell {
          font-size: 0.75rem;
          color: #666;
          text-transform: capitalize;
        }

        .amount {
          font-weight: 700;
          color: #10b981;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-badge.refunded {
          background: #d1fae5;
          color: #059669;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-badge.rejected {
          background: #fee2e2;
          color: #dc2626;
        }

        .action-btn {
          background: #6366f1;
          color: white;
          border: none;
          padding: 6px 14px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.75rem;
        }

        .action-btn:hover {
          background: #4f46e5;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          background: white;
          border-radius: 12px;
          margin-top: 16px;
        }

        .empty-icon {
          font-size: 3rem;
          color: #e0e0e0;
          margin-bottom: 12px;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin: 16px 0;
          background: white;
          padding: 16px;
          border-radius: 12px;
        }

        .page-btn {
          background: #6366f1;
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .page-btn:hover:not(:disabled) {
          background: #4f46e5;
        }

        .page-btn:disabled {
          background: #e0e0e0;
          cursor: not-allowed;
        }

        .page-info {
          font-weight: 600;
          color: #666;
          font-size: 0.875rem;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e0e0e0;
          background: #fafafa;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 700;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e0e0e0;
        }

        .modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
        }

        .status-section {
          text-align: center;
          margin-bottom: 20px;
        }

        .large-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .large-badge.refunded {
          background: #10b981;
          color: white;
        }

        .large-badge.pending {
          background: #f59e0b;
          color: white;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .detail-column {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .detail-group {
          background: #fafafa;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }

        .detail-group h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid #e0e0e0;
        }

        .detail-row {
          margin-bottom: 10px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-row .label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #666;
          text-transform: uppercase;
        }

        .detail-row .value {
          color: #1a1a1a;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .amount-lg {
          font-size: 1.25rem;
          font-weight: 700;
          color: #10b981;
        }

        .products-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .product-item {
          padding: 6px 0;
          border-bottom: 1px solid #e0e0e0;
          font-size: 0.875rem;
        }

        .image-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #6366f1;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
        }

        .image-link:hover {
          color: #4f46e5;
        }

        .refund-form {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 12px;
          border: 2px solid #6366f1;
          margin-top: 20px;
        }

        .form-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 16px 0;
          padding-bottom: 12px;
          border-bottom: 2px solid #e0e0e0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .form-input,
        .form-select {
          padding: 10px 12px;
          border: 1px solid #d0d0d0;
          border-radius: 8px;
          font-size: 0.875rem;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-select:focus {
          border-color: #6366f1;
        }

        .file-input {
          padding: 8px;
          border: 2px dashed #d0d0d0;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .file-input:hover {
          border-color: #6366f1;
        }

        .file-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .form-hint {
          font-size: 0.75rem;
          color: #666;
          font-style: italic;
        }

        .image-preview {
          position: relative;
          margin-top: 12px;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #10b981;
        }

        .image-preview img {
          width: 100%;
          max-height: 200px;
          object-fit: contain;
          display: block;
        }

        .remove-image {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
        }

        .remove-image:hover {
          background: #dc2626;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .btn-submit {
          flex: 1;
          background: #10b981;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .btn-submit:hover:not(:disabled) {
          background: #059669;
        }

        .btn-submit:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .btn-cancel {
          background: #6c757d;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .btn-refund,
        .btn-reject {
          flex: 1;
          padding: 12px 16px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          font-size: 0.875rem;
        }

        .btn-refund {
          background: #10b981;
          color: white;
        }

        .btn-refund:hover {
          background: #059669;
        }

        .btn-reject {
          background: #ef4444;
          color: white;
        }

        .btn-reject:hover {
          background: #dc2626;
        }

        .refund-success {
          background: #d1fae5;
          border: 1px solid #a7f3d0;
          padding: 14px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #059669;
          font-weight: 600;
          margin-top: 20px;
        }

        @media (max-width: 768px) {
          .cancel-report-container {
            padding: 12px;
          }

          .report-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filters-row {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-buttons {
            flex-wrap: wrap;
          }

          .search-wrapper,
          .date-wrapper {
            width: 100%;
          }

          .table-container {
            overflow-x: auto;
          }

          .report-table {
            min-width: 800px;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons,
          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderCancelReport;
